import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { API_ENDPOINTS, API_BASE_URL, apiClient } from '../config/api';
import { SERVICE_TYPE_OPTIONS } from '../data/serviceTypes';
import { formatUSPhoneInput, toE164US } from '../utils/phone';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import './ProfilePage.css';
import './SpProfilePage.css';
import NotificationSettingsPage from './NotificationSettingsPage';
import SettingsPage from './SettingsPage';
import PrivacyPolicyPage from './PrivacyPolicyPage';
import TermsOfServicePage from './TermsOfServicePage';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];

// Full state name for display next to a stateCode (address section shows the
// derived State read-only, matching the buyer/seller Address pattern).
const getStateNameFromCode = (code) => {
  const match = US_STATES.find(s => s.code === code);
  return match ? match.name : (code || '');
};

const getFirebaseToken = () =>
  new Promise((resolve, reject) => {
    if (auth.currentUser) {
      auth.currentUser.getIdToken(true).then(resolve).catch(reject);
    } else {
      const unsub = onAuthStateChanged(auth, (user) => {
        unsub();
        if (user) user.getIdToken(true).then(resolve).catch(reject);
        else reject(new Error('Not authenticated'));
      });
      setTimeout(() => reject(new Error('Auth timeout')), 10000);
    }
  });

// Resolve a backend profilePhotoUrl into something an <img> can actually
// render. When the photo was uploaded as bytes, the backend returns a
// relative, JWT-gated path (e.g. /api/v1/users/{userId}/photo) that a plain
// <img src> can't load (no auth header support) — so fetch it ourselves with
// the Bearer token and convert to a base64 data URI, same blob+FileReader
// pattern already used for this exact endpoint shape in SpDashboardHeader.js.
// Externally-hosted URLs (http(s)://) and data: URIs pass through unchanged.
const resolvePhotoPreview = async (rawUrl, token) => {
  if (!rawUrl) return '';
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://') || rawUrl.startsWith('data:')) {
    return rawUrl;
  }
  try {
    const absoluteUrl = `${API_BASE_URL}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
    const res = await apiClient.get(absoluteUrl, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(res.data);
    });
  } catch (error) {
    console.warn('[SpProfilePage] Failed to fetch profile photo:', error);
    return '';
  }
};

const SpProfilePage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus]       = useState({ type: '', message: '' });
  // Left sidenav section for "My Profile" — Notification Settings, Security &
  // Settings, Privacy Policy, and Terms of Service used to be separate items
  // in the profile-menu dropdown (see SpDashboardHeader.js); they now live
  // here as sections instead.
  const [activeProfileSection, setActiveProfileSection] = useState('info');
  // Resolved, renderable avatar src (absolute URL, data: URI, or base64 from
  // an auth-fetched blob) — kept separate from formData.profilePhotoUrl, which
  // must stay the raw backend value (short path/URL) so it's safe to PATCH back.
  const [photoPreview, setPhotoPreview] = useState('');
  // Same resolved-preview treatment as photoPreview above, for the company logo.
  const [companyLogoPreview, setCompanyLogoPreview] = useState('');

  // Parent-company linkage (INDIVIDUAL vendors only). parentCompanyUserId is
  // the FK sent on PATCH when the user picks/unlinks; parentCompany carries
  // the denormalised summary the backend returns on GET + PATCH so the card
  // can render without a second request. originalParentCompanyUserId is the
  // snapshot at load/save so we only PATCH the FK when it actually changed.
  const [parentCompanyUserId, setParentCompanyUserId] = useState('');
  const [parentCompany, setParentCompany] = useState(null);
  const [originalParentCompanyUserId, setOriginalParentCompanyUserId] = useState('');
  // Resolved parent-company logo src — same two-tier pattern as photoPreview
  // above: absolute URLs / data URIs pass through, auth-gated relative paths
  // (/api/v1/users/{id}/company-logo) get fetched with Bearer and inlined
  // as a data URI so a plain <img> can render them.
  const [parentCompanyLogoPreview, setParentCompanyLogoPreview] = useState('');

  // "Find my company" picker modal state. searchResults rows:
  // {userId, businessName, companyLogoUrl, city, state, email, phone}.
  const [showCompanyPickerModal, setShowCompanyPickerModal] = useState(false);
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [companySearchResults, setCompanySearchResults] = useState([]);
  const [companySearching, setCompanySearching] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    // Read-only — Firebase auth identity, never sent on PATCH (mirrors ProfilePage.js).
    email: '',
    phone: '',
    profilePhotoUrl: '',
    // "INDIVIDUAL" or "COMPANY" — read-only, never sent on PATCH.
    vendorType: '',
    companyLogoUrl: '',
    addressLine1: '',
    city: '',
    state: '',
    zipCode: '',
    businessName: '',
    providerType: '',
    serviceTypes: [],
    licenseNumber: '',
    licenseState: '',
    insuranceProvider: '',
    insurancePolicyNumber: '',
    insuranceExpiryDate: '',
    bonded: false,
    serviceRadiusMiles: '',
    coverageArea: [],
    servicesOffered: [],
    certifications: [],
    languages: [],
    hourlyRate: '',
    bio: '',
    websiteUrl: '',
    yearsOfExperience: '',
    socialLinks: { linkedin: '', instagram: '', facebook: '' },
  });

  const [tagInputs, setTagInputs] = useState({
    coverageArea: '',
    servicesOffered: '',
    certifications: '',
    languages: '',
  });

  // Service-type picker catalog — seeded with the offline fallback, overwritten
  // by the live GET /api/v1/service-types fetch below (mirrors CreateServiceRequestPage.js).
  const [serviceTypeOptions, setServiceTypeOptions] = useState(SERVICE_TYPE_OPTIONS);

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line
  }, []);

  // Debounced company search for the "Find my company" picker modal. Fires
  // only while the modal is open and the query is non-trivial — spares the
  // backend from a flood of 1-char lookups and matches the SP mobile
  // ViewProfileScreen debounce window.
  useEffect(() => {
    if (!showCompanyPickerModal) return undefined;
    const trimmed = companySearchQuery.trim();
    if (trimmed.length < 2) {
      setCompanySearchResults([]);
      setCompanySearching(false);
      return undefined;
    }
    let cancelled = false;
    setCompanySearching(true);
    const timer = setTimeout(async () => {
      try {
        const token = await getFirebaseToken();
        const resp = await apiClient.get(API_ENDPOINTS.spCompanySearch, {
          headers: { Authorization: `Bearer ${token}` },
          params: { q: trimmed, limit: 20 },
        });
        if (cancelled) return;
        const rows = Array.isArray(resp.data) ? resp.data : (resp.data?.content || resp.data?.data || []);
        setCompanySearchResults(Array.isArray(rows) ? rows : []);
      } catch (error) {
        if (!cancelled) {
          console.warn('[SpProfilePage] Company search failed:', error);
          setCompanySearchResults([]);
        }
      } finally {
        if (!cancelled) setCompanySearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [companySearchQuery, showCompanyPickerModal]);

  // Picker-select → stage the new link locally (actual PATCH fires on Save).
  // We also pre-populate parentCompany from the picker row so the card can
  // render immediately without waiting for a re-fetch, and resolve the
  // auth-gated company-logo URL into something an <img> can actually render.
  const handleCompanyPick = async (row) => {
    setParentCompanyUserId(row.userId);
    setParentCompany({
      userId: row.userId,
      businessName: row.businessName,
      companyLogoUrl: row.companyLogoUrl,
      email: row.email,
      phone: row.phone,
      city: row.city,
      state: row.state,
    });
    setShowCompanyPickerModal(false);
    setCompanySearchQuery('');
    setCompanySearchResults([]);
    try {
      const token = await getFirebaseToken();
      setParentCompanyLogoPreview(await resolvePhotoPreview(row.companyLogoUrl, token));
    } catch {
      setParentCompanyLogoPreview('');
    }
  };

  const handleCompanyUnlink = () => {
    setParentCompanyUserId('');
    setParentCompany(null);
    setParentCompanyLogoPreview('');
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const resp = await apiClient.get(API_ENDPOINTS.serviceTypes, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const catalog = Array.isArray(resp.data) ? resp.data : (resp.data?.content || resp.data?.data || []);
        if (!cancelled && catalog.length > 0) {
          setServiceTypeOptions(catalog.map(t => ({ id: t.id, name: t.label })));
        }
      } catch (error) {
        console.warn('[SpProfilePage] Failed to fetch service types, using fallback:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfile = async () => {
    setLoading(true);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(API_ENDPOINTS.spProfile, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data?.data || res.data?.content || res.data || {};
      setFormData({
        firstName:             d.firstName             || '',
        lastName:              d.lastName              || '',
        email:                 d.email                 || '',
        phone:                 d.phone                 || '',
        profilePhotoUrl:       d.profilePhotoUrl       || '',
        vendorType:            d.vendorType            || '',
        companyLogoUrl:        d.companyLogoUrl        || '',
        addressLine1:          d.addressLine1          || '',
        city:                  d.city                  || '',
        state:                 d.state                 || '',
        zipCode:               d.zipCode               || '',
        businessName:          d.businessName          || '',
        providerType:          d.providerType          || '',
        serviceTypes:          Array.isArray(d.serviceTypes) ? d.serviceTypes : [],
        licenseNumber:         d.licenseNumber         || '',
        licenseState:          d.licenseState          || '',
        insuranceProvider:     d.insuranceProvider     || '',
        insurancePolicyNumber: d.insurancePolicyNumber || '',
        insuranceExpiryDate:   d.insuranceExpiryDate   ? d.insuranceExpiryDate.slice(0, 10) : '',
        bonded:                !!d.bonded,
        serviceRadiusMiles:    d.serviceRadiusMiles    != null ? String(d.serviceRadiusMiles) : '',
        coverageArea:          Array.isArray(d.coverageArea)    ? d.coverageArea    : [],
        servicesOffered:       Array.isArray(d.servicesOffered) ? d.servicesOffered : [],
        certifications:        Array.isArray(d.certifications)  ? d.certifications  : [],
        languages:             Array.isArray(d.languages)       ? d.languages       : [],
        hourlyRate:            d.hourlyRate            != null ? String(d.hourlyRate) : '',
        bio:                   d.bio                   || '',
        websiteUrl:            d.websiteUrl            || '',
        yearsOfExperience:     d.yearsOfExperience     != null ? String(d.yearsOfExperience) : '',
        socialLinks: {
          linkedin:  d.socialLinks?.linkedin  || '',
          instagram: d.socialLinks?.instagram || '',
          facebook:  d.socialLinks?.facebook  || '',
        },
      });
      // Parent-company linkage — backend returns both the FK and (when it
      // resolves) a denormalised summary. Null parentCompany on a non-null FK
      // means the parent was deleted; the view renders a graceful empty state.
      const loadedParentId = d.parentCompanyUserId || '';
      setParentCompanyUserId(loadedParentId);
      setOriginalParentCompanyUserId(loadedParentId);
      setParentCompany(d.parentCompany || null);
      setParentCompanyLogoPreview(
        await resolvePhotoPreview(d.parentCompany?.companyLogoUrl, token),
      );
      setPhotoPreview(await resolvePhotoPreview(d.profilePhotoUrl, token));
      setCompanyLogoPreview(await resolvePhotoPreview(d.companyLogoUrl, token));
    } catch {
      setStatus({ type: 'error', message: 'Failed to load profile. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && name === 'bonded') {
      setFormData(prev => ({ ...prev, bonded: checked }));
    } else if (['linkedin', 'instagram', 'facebook'].includes(name)) {
      setFormData(prev => ({ ...prev, socialLinks: { ...prev.socialLinks, [name]: value } }));
    } else if (name === 'serviceRadiusMiles' || name === 'hourlyRate') {
      setFormData(prev => ({ ...prev, [name]: value.replace(/[^0-9.]/g, '') }));
    } else if (name === 'yearsOfExperience') {
      setFormData(prev => ({ ...prev, [name]: value.replace(/[^0-9]/g, '').slice(0, 2) }));
    } else if (name === 'phone') {
      setFormData(prev => ({ ...prev, phone: formatUSPhoneInput(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Google Places pick (mirrors ProfilePage.js's buyer/seller Address
  // handling): State is derived from the pick only, never a standalone
  // dropdown, so it can't be saved without a matching street/city/zip.
  const handleAddressSelect = (parsed) => {
    setFormData(prev => ({
      ...prev,
      addressLine1: parsed.street || prev.addressLine1,
      ...(parsed.stateCode && {
        state: parsed.stateCode,
        city: parsed.city || '',
        zipCode: parsed.zip || '',
      }),
    }));
  };

  // Toggle by full catalog option so we can match existing entries whether
  // the profile stored a canonical id (SP mobile writes ids) or a label
  // (older name-based writes from this page).
  const toggleServiceType = (opt) => {
    setFormData(prev => {
      const current = prev.serviceTypes || [];
      const existing = current.find(t => t === opt.id || t === opt.name);
      return {
        ...prev,
        serviceTypes: existing
          ? current.filter(t => t !== existing)
          : [...current, opt.name],
      };
    });
  };

  const addTag = (field) => {
    const val = tagInputs[field].trim();
    if (!val) return;
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(val) ? prev[field] : [...prev[field], val],
    }));
    setTagInputs(prev => ({ ...prev, [field]: '' }));
  };

  const removeTag = (field, val) => {
    setFormData(prev => ({ ...prev, [field]: prev[field].filter(t => t !== val) }));
  };

  const handleTagKeyDown = (e, field) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(field);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = await getFirebaseToken();
      const photoForm = new FormData();
      photoForm.append('photo', file);
      const res = await apiClient.post(API_ENDPOINTS.spProfilePhoto, photoForm, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': undefined,
        },
      });
      const returned = res.data?.data || res.data || {};
      const remoteUrl = returned.profilePhotoUrl || returned.photoUrl || returned.url || '';

      // Raw value only — this is what gets PATCHed back later, so it must stay
      // a short path/URL (backend enforces profilePhotoUrl <= 1024 chars), never
      // the base64 preview below.
      if (remoteUrl) {
        setFormData(prev => ({ ...prev, profilePhotoUrl: remoteUrl }));
      }

      // Use FileReader for instant base64 preview — same pattern as ProfilePage for other roles.
      // The remote CDN URL may not be immediately accessible, so base64 ensures the image
      // displays right away. Rendered via photoPreview only (display-only state), not formData.
      // Also updates localStorage so DashboardHeader avatar reflects it.
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Image = reader.result;
        setPhotoPreview(base64Image);
        try {
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          stored.profilePhotoBase64 = base64Image;
          if (remoteUrl) stored.profilePhotoUrl = remoteUrl;
          localStorage.setItem('user', JSON.stringify(stored));
        } catch { /* ignore */ }
      };
      reader.readAsDataURL(file);
    } catch {
      setStatus({ type: 'error', message: 'Photo upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = await getFirebaseToken();
      const logoForm = new FormData();
      logoForm.append('logo', file);
      const res = await apiClient.post(API_ENDPOINTS.spProfileCompanyLogo, logoForm, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': undefined,
        },
      });
      const returned = res.data?.data || res.data || {};
      const remoteUrl = returned.companyLogoUrl || '';

      if (remoteUrl) {
        setFormData(prev => ({ ...prev, companyLogoUrl: remoteUrl }));
      }

      const reader = new FileReader();
      reader.onloadend = () => setCompanyLogoPreview(reader.result);
      reader.readAsDataURL(file);
    } catch {
      setStatus({ type: 'error', message: 'Logo upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setStatus({ type: '', message: '' });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setStatus({ type: '', message: '' });
    loadProfile();
  };

  const handleSave = async () => {
    const isCompanyVendor = formData.vendorType === 'COMPANY';
    if (isCompanyVendor) {
      if (!formData.businessName.trim()) {
        setStatus({ type: 'error', message: 'Business name is required.' });
        return;
      }
    } else if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setStatus({ type: 'error', message: 'First name and last name are required.' });
      return;
    }
    setSaving(true);
    setStatus({ type: '', message: '' });
    try {
      const token = await getFirebaseToken();
      const payload = {
        firstName:             formData.firstName             || null,
        lastName:              formData.lastName              || null,
        phone:                 formData.phone ? toE164US(formData.phone) : null,
        profilePhotoUrl:       formData.profilePhotoUrl       || null,
        addressLine1:          formData.addressLine1          || null,
        city:                  formData.city                  || null,
        state:                 formData.state                 || null,
        zipCode:               formData.zipCode               || null,
        businessName:          formData.businessName          || null,
        // Mirrors mobile: providerType is the primary category, kept in sync
        // with the (multi-select) serviceTypes list by taking its first entry.
        providerType:          formData.serviceTypes.length > 0 ? formData.serviceTypes[0] : null,
        serviceTypes:          formData.serviceTypes.length > 0 ? formData.serviceTypes : null,
        licenseNumber:         formData.licenseNumber         || null,
        licenseState:          formData.licenseState          || null,
        insuranceProvider:     formData.insuranceProvider     || null,
        insurancePolicyNumber: formData.insurancePolicyNumber || null,
        insuranceExpiryDate:   formData.insuranceExpiryDate   || null,
        bonded:                formData.bonded,
        serviceRadiusMiles:    formData.serviceRadiusMiles ? parseFloat(formData.serviceRadiusMiles) : null,
        coverageArea:          formData.coverageArea.length > 0 ? formData.coverageArea : null,
        servicesOffered:       formData.servicesOffered.length > 0 ? formData.servicesOffered : null,
        certifications:        formData.certifications.length > 0 ? formData.certifications : null,
        languages:             formData.languages.length > 0 ? formData.languages : null,
        hourlyRate:            formData.hourlyRate ? parseFloat(formData.hourlyRate) : null,
        bio:                   formData.bio                   || null,
        websiteUrl:            formData.websiteUrl            || null,
        yearsOfExperience:     formData.yearsOfExperience ? parseInt(formData.yearsOfExperience, 10) : null,
        socialLinks: {
          linkedin:  formData.socialLinks.linkedin  || null,
          instagram: formData.socialLinks.instagram || null,
          facebook:  formData.socialLinks.facebook  || null,
        },
      };
      // parentCompanyUserId — only send when the user changed it (picker select
      // or Unlink). Omitting it is a no-op on the backend; sending "" clears
      // the FK (unlink). Matches the SP mobile ViewProfileScreen pattern.
      if (parentCompanyUserId !== originalParentCompanyUserId) {
        payload.parentCompanyUserId = parentCompanyUserId;
      }
      const resp = await apiClient.patch(API_ENDPOINTS.spProfile, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Lock in the new parent-company value so a subsequent save without
      // another picker change doesn't resend it. Prefer the backend's
      // post-commit snapshot (including the resolved parentCompany summary).
      setOriginalParentCompanyUserId(parentCompanyUserId);
      const postResp = resp?.data?.data || resp?.data?.content || resp?.data || {};
      if (postResp.parentCompany !== undefined) {
        setParentCompany(postResp.parentCompany || null);
      }
      setIsEditing(false);
      setStatus({ type: 'success', message: 'Profile updated successfully!' });
      setTimeout(() => setStatus({ type: '', message: '' }), 3000);
    } catch {
      setStatus({ type: 'error', message: 'Failed to save profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <main className="profile-main-content">
          <div className="profile-container">
            <p className="loading-text">Loading profile...</p>
          </div>
        </main>
      </div>
    );
  }

  const initials = `${formData.firstName?.[0] || ''}${formData.lastName?.[0] || ''}`.toUpperCase() || 'SP';
  const isCompanyVendor = formData.vendorType === 'COMPANY';

  const profileSidenavItems = [
    {
      id: 'info', label: 'Profile Info',
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>),
    },
    {
      id: 'notifications', label: 'Notification Settings',
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>),
    },
    {
      id: 'settings', label: 'Security & Settings',
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>),
    },
    {
      id: 'privacy', label: 'Privacy Policy',
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>),
    },
    {
      id: 'terms', label: 'Terms of Service',
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>),
    },
  ];

  return (
    <div className="profile-page">
      <main className="profile-main-content">
        <div className="my-profile-layout">
          <aside className="my-profile-sidenav">
            <div className="my-profile-sidenav-title">My Profile</div>
            {profileSidenavItems.map(item => (
              <button
                key={item.id}
                type="button"
                className={`my-profile-sidenav-item${activeProfileSection === item.id ? ' current' : ''}`}
                onClick={() => setActiveProfileSection(item.id)}
              >
                <span className="my-profile-sidenav-icon">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </aside>

          <div className="my-profile-content">
          {activeProfileSection === 'info' && (
        <div className="profile-container">

          {/* ── Header: title + avatar + photo upload ── */}
          <div className="profile-header">
            <button type="button" className="profile-back-btn" onClick={() => navigate(-1)}>
              <span>←</span>
              <span>Back</span>
            </button>
            <h1 className="profile-title">My Profile</h1>
            <p className="profile-subtitle">View and manage your personal information</p>
            <div className="profile-avatar-container">
              <div className="profile-avatar">
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Profile"
                    className="profile-avatar-image"
                  />
                ) : (
                  <div className="spp-avatar-initials">{initials}</div>
                )}
                {uploading && (
                  <div className="photo-uploading-overlay">
                    <span>Uploading...</span>
                  </div>
                )}
              </div>
              <div className="image-upload-section">
                <label
                  htmlFor="sp-profile-image-input"
                  className={`image-upload-label ${uploading ? 'disabled' : ''}`}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  {uploading ? 'Uploading...' : 'Change Photo'}
                </label>
                <input
                  id="sp-profile-image-input"
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="image-upload-input"
                  disabled={uploading}
                />
                {/* Edit / Save / Cancel next to Change Photo so they're visible on
                    page load — same handlers as the bottom profile-actions bar. */}
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      className="image-upload-label profile-inline-action"
                      onClick={handleSave}
                      disabled={saving || uploading}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      className="image-upload-label profile-inline-action profile-inline-action--secondary"
                      onClick={handleCancel}
                      disabled={saving || uploading}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="image-upload-label profile-inline-action"
                    onClick={handleEdit}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                    Edit Profile
                  </button>
                )}
              </div>
              {isCompanyVendor && formData.businessName && (
                <p className="spp-header-business">{formData.businessName}</p>
              )}
              <span className="spp-role-badge">Service Provider</span>
            </div>
          </div>

          {/* ── Status message ── */}
          {status.message && (
            <div className={`profile-status-message ${status.type}`}>
              {status.type === 'success' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              )}
              {status.type === 'error' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              )}
              <span>{status.message}</span>
            </div>
          )}

          {/* ── Content sections ── */}
          <div className="profile-content">

            {/* Personal Information */}
            <div className="profile-info-section">
              <h2 className="section-title">Personal Information</h2>
              <div className={`info-grid${isCompanyVendor ? ' spp-personal-grid' : ''}`}>

                {!isCompanyVendor && (
                  <>
                    <div className="info-field">
                      <label className="info-label">First Name</label>
                      {isEditing ? (
                        <input type="text" name="firstName" className="profile-input"
                          value={formData.firstName} onChange={handleChange} placeholder="First name" />
                      ) : (
                        <p className="info-value">{formData.firstName || '—'}</p>
                      )}
                    </div>

                    <div className="info-field">
                      <label className="info-label">Last Name</label>
                      {isEditing ? (
                        <input type="text" name="lastName" className="profile-input"
                          value={formData.lastName} onChange={handleChange} placeholder="Last name" />
                      ) : (
                        <p className="info-value">{formData.lastName || '—'}</p>
                      )}
                    </div>
                  </>
                )}

                {/* Read-only — Firebase auth identity, same as ProfilePage.js's Email field.
                    Shown for both Individual and Company, no isEditing branch. */}
                <div className="info-field">
                  <label className="info-label">Email</label>
                  <p className="info-value">{formData.email || '—'}</p>
                </div>

                <div className="info-field">
                  <label className="info-label">Phone</label>
                  {isEditing ? (
                    <input type="tel" name="phone" className="profile-input"
                      value={formData.phone} onChange={handleChange} placeholder="Phone number" />
                  ) : (
                    <p className="info-value">{formData.phone || '—'}</p>
                  )}
                </div>

                {isCompanyVendor && (
                  <>
                    <div className="info-field">
                      <label className="info-label">Business Name</label>
                      {isEditing ? (
                        <input type="text" name="businessName" className="profile-input"
                          value={formData.businessName} onChange={handleChange} placeholder="Business / company name" />
                      ) : (
                        <p className="info-value">{formData.businessName || '—'}</p>
                      )}
                    </div>

                    <div className="info-field spp-field-full">
                      <label className="info-label">Company Logo</label>
                      <div className="profile-avatar-container">
                        <div className="profile-avatar spp-logo-avatar">
                          {companyLogoPreview ? (
                            <img src={companyLogoPreview} alt="Company logo" className="profile-avatar-image" />
                          ) : (
                            <div className="spp-avatar-initials">{(formData.businessName?.[0] || 'C').toUpperCase()}</div>
                          )}
                          {uploading && (
                            <div className="photo-uploading-overlay">
                              <span>Uploading...</span>
                            </div>
                          )}
                        </div>
                        <div className="image-upload-section">
                          <label
                            htmlFor="sp-company-logo-input"
                            className={`image-upload-label ${uploading ? 'disabled' : ''}`}
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                              <circle cx="12" cy="13" r="4" />
                            </svg>
                            {uploading ? 'Uploading...' : (companyLogoPreview ? 'Change Logo' : 'Add Logo')}
                          </label>
                          <input
                            id="sp-company-logo-input"
                            type="file"
                            accept="image/*"
                            onChange={handleLogoChange}
                            className="image-upload-input"
                            disabled={uploading}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="info-field spp-field-full">
                  <label className="info-label">Bio</label>
                  {isEditing ? (
                    <textarea name="bio" className="profile-input spp-textarea"
                      rows={4} value={formData.bio} onChange={handleChange}
                      placeholder="Short bio about yourself…" />
                  ) : (
                    <p className="info-value spp-value-bio">{formData.bio || '—'}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">Website URL</label>
                  {isEditing ? (
                    <input type="text" name="websiteUrl" className="profile-input"
                      value={formData.websiteUrl} onChange={handleChange} placeholder="https://yoursite.com" />
                  ) : (
                    <p className="info-value">{formData.websiteUrl || '—'}</p>
                  )}
                </div>

              </div>
            </div>

            {/* Service Details */}
            <div className="profile-info-section">
              <h2 className="section-title">Service Details</h2>
              <div className="info-grid">

                <div className="info-field spp-field-full">
                  <label className="info-label">
                    Service Types
                    {formData.serviceTypes.length > 0 && (
                      <span className="spp-count-badge">{formData.serviceTypes.length}</span>
                    )}
                  </label>
                  {isEditing ? (
                    <div className="spp-pill-grid">
                      {serviceTypeOptions.map(opt => {
                        const selected = formData.serviceTypes.some(
                          t => t === opt.id || t === opt.name
                        );
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            className={`spp-pill${selected ? ' spp-pill-selected' : ''}`}
                            onClick={() => toggleServiceType(opt)}
                          >
                            {selected && (
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            )}
                            {opt.name}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="info-value spp-value-pills">
                      {formData.serviceTypes.length > 0
                        ? formData.serviceTypes.map(t => {
                            // Resolve id → label via the catalog; fall back to
                            // the raw value for older name-based entries or when
                            // the catalog hasn't loaded yet.
                            const opt = serviceTypeOptions.find(
                              o => o.id === t || o.name === t
                            );
                            return (
                              <span key={t} className="spp-view-pill">{opt?.name || t}</span>
                            );
                          })
                        : <span>—</span>}
                    </div>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">Years of Experience</label>
                  {isEditing ? (
                    <input type="text" name="yearsOfExperience" className="profile-input"
                      value={formData.yearsOfExperience} onChange={handleChange}
                      placeholder="e.g. 5" maxLength={2} />
                  ) : (
                    <p className="info-value">{formData.yearsOfExperience || '—'}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">Hourly Rate ($)</label>
                  {isEditing ? (
                    <input type="text" name="hourlyRate" className="profile-input"
                      value={formData.hourlyRate} onChange={handleChange} placeholder="e.g. 75" />
                  ) : (
                    <p className="info-value">{formData.hourlyRate ? `$${formData.hourlyRate}` : '—'}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">Service Radius (miles)</label>
                  {isEditing ? (
                    <input type="text" name="serviceRadiusMiles" className="profile-input"
                      value={formData.serviceRadiusMiles} onChange={handleChange} placeholder="e.g. 50" />
                  ) : (
                    <p className="info-value">{formData.serviceRadiusMiles ? `${formData.serviceRadiusMiles} mi` : '—'}</p>
                  )}
                </div>

                {/* Coverage Area tags */}
                <div className="info-field spp-field-full">
                  <label className="info-label">Coverage Area</label>
                  {isEditing ? (
                    <div className="spp-tag-field profile-input">
                      {formData.coverageArea.map(tag => (
                        <span key={tag} className="spp-tag">
                          {tag}
                          <button type="button" className="spp-tag-remove" onClick={() => removeTag('coverageArea', tag)}>×</button>
                        </span>
                      ))}
                      <input
                        className="spp-tag-input"
                        value={tagInputs.coverageArea}
                        onChange={e => setTagInputs(prev => ({ ...prev, coverageArea: e.target.value }))}
                        onKeyDown={e => handleTagKeyDown(e, 'coverageArea')}
                        onBlur={() => addTag('coverageArea')}
                        placeholder={formData.coverageArea.length === 0 ? 'Type city / area + Enter' : 'Add more…'}
                      />
                    </div>
                  ) : (
                    <div className="info-value spp-value-pills">
                      {formData.coverageArea.length > 0
                        ? formData.coverageArea.map(t => <span key={t} className="spp-view-tag">{t}</span>)
                        : <span>—</span>}
                    </div>
                  )}
                </div>

                {/* Services Offered tags */}
                <div className="info-field spp-field-full">
                  <label className="info-label">Services Offered</label>
                  {isEditing ? (
                    <div className="spp-tag-field profile-input">
                      {formData.servicesOffered.map(tag => (
                        <span key={tag} className="spp-tag">
                          {tag}
                          <button type="button" className="spp-tag-remove" onClick={() => removeTag('servicesOffered', tag)}>×</button>
                        </span>
                      ))}
                      <input
                        className="spp-tag-input"
                        value={tagInputs.servicesOffered}
                        onChange={e => setTagInputs(prev => ({ ...prev, servicesOffered: e.target.value }))}
                        onKeyDown={e => handleTagKeyDown(e, 'servicesOffered')}
                        onBlur={() => addTag('servicesOffered')}
                        placeholder={formData.servicesOffered.length === 0 ? 'Type service + Enter' : 'Add more…'}
                      />
                    </div>
                  ) : (
                    <div className="info-value spp-value-pills">
                      {formData.servicesOffered.length > 0
                        ? formData.servicesOffered.map(t => <span key={t} className="spp-view-tag">{t}</span>)
                        : <span>—</span>}
                    </div>
                  )}
                </div>

                {/* Certifications tags */}
                <div className="info-field spp-field-full">
                  <label className="info-label">Certifications</label>
                  {isEditing ? (
                    <div className="spp-tag-field profile-input">
                      {formData.certifications.map(tag => (
                        <span key={tag} className="spp-tag">
                          {tag}
                          <button type="button" className="spp-tag-remove" onClick={() => removeTag('certifications', tag)}>×</button>
                        </span>
                      ))}
                      <input
                        className="spp-tag-input"
                        value={tagInputs.certifications}
                        onChange={e => setTagInputs(prev => ({ ...prev, certifications: e.target.value }))}
                        onKeyDown={e => handleTagKeyDown(e, 'certifications')}
                        onBlur={() => addTag('certifications')}
                        placeholder={formData.certifications.length === 0 ? 'Type certification + Enter' : 'Add more…'}
                      />
                    </div>
                  ) : (
                    <div className="info-value spp-value-pills">
                      {formData.certifications.length > 0
                        ? formData.certifications.map(t => <span key={t} className="spp-view-tag">{t}</span>)
                        : <span>—</span>}
                    </div>
                  )}
                </div>

                {/* Languages tags */}
                <div className="info-field spp-field-full">
                  <label className="info-label">Languages</label>
                  {isEditing ? (
                    <div className="spp-tag-field profile-input">
                      {formData.languages.map(tag => (
                        <span key={tag} className="spp-tag">
                          {tag}
                          <button type="button" className="spp-tag-remove" onClick={() => removeTag('languages', tag)}>×</button>
                        </span>
                      ))}
                      <input
                        className="spp-tag-input"
                        value={tagInputs.languages}
                        onChange={e => setTagInputs(prev => ({ ...prev, languages: e.target.value }))}
                        onKeyDown={e => handleTagKeyDown(e, 'languages')}
                        onBlur={() => addTag('languages')}
                        placeholder={formData.languages.length === 0 ? 'Type language + Enter' : 'Add more…'}
                      />
                    </div>
                  ) : (
                    <div className="info-value spp-value-pills">
                      {formData.languages.length > 0
                        ? formData.languages.map(t => <span key={t} className="spp-view-tag">{t}</span>)
                        : <span>—</span>}
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* License & Insurance */}
            <div className="profile-info-section">
              <h2 className="section-title">License &amp; Insurance</h2>
              <div className="info-grid">

                <div className="info-field">
                  <label className="info-label">License Number</label>
                  {isEditing ? (
                    <input type="text" name="licenseNumber" className="profile-input"
                      value={formData.licenseNumber} onChange={handleChange} placeholder="License number" />
                  ) : (
                    <p className="info-value">{formData.licenseNumber || '—'}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">License State</label>
                  {isEditing ? (
                    <select name="licenseState" className="profile-input"
                      value={formData.licenseState} onChange={handleChange}>
                      <option value="">Select state</option>
                      {US_STATES.map(s => (
                        <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="info-value">{formData.licenseState || '—'}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">Insurance Provider</label>
                  {isEditing ? (
                    <input type="text" name="insuranceProvider" className="profile-input"
                      value={formData.insuranceProvider} onChange={handleChange} placeholder="Provider name" />
                  ) : (
                    <p className="info-value">{formData.insuranceProvider || '—'}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">Insurance Policy #</label>
                  {isEditing ? (
                    <input type="text" name="insurancePolicyNumber" className="profile-input"
                      value={formData.insurancePolicyNumber} onChange={handleChange} placeholder="Policy number" />
                  ) : (
                    <p className="info-value">{formData.insurancePolicyNumber || '—'}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">Insurance Expiry Date</label>
                  {isEditing ? (
                    <input type="date" name="insuranceExpiryDate" className="profile-input"
                      value={formData.insuranceExpiryDate} onChange={handleChange} />
                  ) : (
                    <p className="info-value">{formData.insuranceExpiryDate || '—'}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">Bonded</label>
                  {isEditing ? (
                    <label className="spp-toggle-row">
                      <span className="spp-toggle-wrap">
                        <input type="checkbox" name="bonded" checked={formData.bonded} onChange={handleChange} className="spp-toggle-input" />
                        <span className="spp-toggle-track"><span className="spp-toggle-thumb" /></span>
                      </span>
                      <span className="spp-toggle-text">{formData.bonded ? 'Yes' : 'No'}</span>
                    </label>
                  ) : (
                    <p className="info-value">{formData.bonded ? 'Yes' : 'No'}</p>
                  )}
                </div>

              </div>
            </div>

            {/* Address */}
            <div className="profile-info-section">
              <h2 className="section-title">Address</h2>
              <div className="info-grid">

                <div className="info-field spp-field-full">
                  <label className="info-label">Street Address</label>
                  {isEditing ? (
                    <AddressAutocomplete
                      value={formData.addressLine1}
                      onChange={(text) => handleChange({ target: { name: 'addressLine1', value: text } })}
                      onAddressSelect={handleAddressSelect}
                      placeholder="Street address"
                      inputClassName="profile-input"
                    />
                  ) : (
                    <p className="info-value">{formData.addressLine1 || '—'}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">City</label>
                  {isEditing ? (
                    <input type="text" name="city" className="profile-input"
                      value={formData.city} onChange={handleChange} placeholder="City" />
                  ) : (
                    <p className="info-value">{formData.city || '—'}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">State</label>
                  {isEditing ? (
                    <select name="state" className="profile-input"
                      value={formData.state} onChange={handleChange}>
                      <option value="">Select state</option>
                      {US_STATES.map(s => (
                        <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="info-value">{getStateNameFromCode(formData.state) || '—'}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">ZIP Code</label>
                  {isEditing ? (
                    <input type="text" name="zipCode" className="profile-input"
                      value={formData.zipCode} onChange={handleChange} placeholder="ZIP" maxLength={10} />
                  ) : (
                    <p className="info-value">{formData.zipCode || '—'}</p>
                  )}
                </div>

              </div>
            </div>


            {/* My Company — optional parent-company mapping for INDIVIDUAL SPs.
                Shows the mapped company card (or an empty state) in view mode;
                in edit mode, adds Change / Unlink affordances that open the
                "Find my company" picker modal defined below. Mirrors the SP
                mobile ViewProfileScreen "MY COMPANY (OPTIONAL)" section. */}
            {!isCompanyVendor && formData.vendorType && (
              <div className="profile-info-section">
                <h2 className="section-title">Company Information</h2>
                <div className="info-grid">
                  <div className="info-field" style={{ gridColumn: '1 / -1' }}>
                    {parentCompanyUserId && parentCompany ? (
                      <div className="sp-parent-company-card">
                        {parentCompanyLogoPreview ? (
                          <img
                            src={parentCompanyLogoPreview}
                            alt={parentCompany.businessName || 'Company'}
                            className="sp-parent-company-logo"
                          />
                        ) : (
                          <div className="sp-parent-company-logo sp-parent-company-logo-fallback">
                            {(parentCompany.businessName || 'C').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="sp-parent-company-body">
                          <p className="sp-parent-company-name">
                            {parentCompany.businessName || 'Company'}
                          </p>
                          {(parentCompany.city || parentCompany.state) && (
                            <p className="sp-parent-company-meta">
                              {[parentCompany.city, parentCompany.state].filter(Boolean).join(', ')}
                            </p>
                          )}
                          {parentCompany.email && (
                            <p className="sp-parent-company-meta">{parentCompany.email}</p>
                          )}
                        </div>
                      </div>
                    ) : parentCompanyUserId ? (
                      // FK still set but summary missing — stale (company deleted).
                      <p className="info-value">Linked company is no longer available.</p>
                    ) : (
                      <p className="info-value">Not linked to a company</p>
                    )}

                    {isEditing && (
                      <div className="sp-parent-company-actions">
                        <button
                          type="button"
                          className="btn-cancel"
                          onClick={() => {
                            setCompanySearchQuery('');
                            setCompanySearchResults([]);
                            setShowCompanyPickerModal(true);
                          }}
                        >
                          {parentCompanyUserId ? 'Change' : 'Find my company'}
                        </button>
                        {parentCompanyUserId && (
                          <button
                            type="button"
                            className="btn-cancel"
                            onClick={handleCompanyUnlink}
                          >
                            Unlink
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Social Links */}
            <div className="profile-info-section">
              <h2 className="section-title">Social Links</h2>
              <div className="info-grid">

                <div className="info-field">
                  <label className="info-label">LinkedIn</label>
                  {isEditing ? (
                    <input type="text" name="linkedin" className="profile-input"
                      value={formData.socialLinks.linkedin} onChange={handleChange}
                      placeholder="LinkedIn profile URL" />
                  ) : (
                    <p className="info-value">{formData.socialLinks.linkedin || '—'}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">Instagram</label>
                  {isEditing ? (
                    <input type="text" name="instagram" className="profile-input"
                      value={formData.socialLinks.instagram} onChange={handleChange}
                      placeholder="Instagram profile URL" />
                  ) : (
                    <p className="info-value">{formData.socialLinks.instagram || '—'}</p>
                  )}
                </div>

                <div className="info-field">
                  <label className="info-label">Facebook</label>
                  {isEditing ? (
                    <input type="text" name="facebook" className="profile-input"
                      value={formData.socialLinks.facebook} onChange={handleChange}
                      placeholder="Facebook profile URL" />
                  ) : (
                    <p className="info-value">{formData.socialLinks.facebook || '—'}</p>
                  )}
                </div>

              </div>
            </div>

            {/* Account — always read-only, regardless of isEditing */}
            <div className="profile-info-section">
              <h2 className="section-title">Account</h2>
              <div className="info-grid">
                <div className="info-field">
                  <label className="info-label">Vendor Type</label>
                  <p className="info-value">{isCompanyVendor ? 'Company' : 'Individual'}</p>
                  <p className="info-help">
                    Set during registration — <a href="mailto:support@rialty.ai?subject=Vendor%20Type%20Change%20Request">contact support</a> to change.
                  </p>
                </div>
              </div>
            </div>

          </div>{/* end profile-content */}

          {/* ── Actions: Edit / Save + Cancel ── */}
          <div className="profile-actions">
            {isEditing ? (
              <>
                <button className="btn-save" onClick={handleSave} disabled={saving || uploading}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button className="btn-cancel" onClick={handleCancel} disabled={saving || uploading}>
                  Cancel
                </button>
              </>
            ) : (
              <button className="btn-edit" onClick={handleEdit}>
                Edit Profile
              </button>
            )}
          </div>

        </div>
          )}

          {activeProfileSection === 'notifications' && <NotificationSettingsPage />}
          {activeProfileSection === 'settings' && <SettingsPage />}
          {activeProfileSection === 'privacy' && <PrivacyPolicyPage showBackButton />}
          {activeProfileSection === 'terms' && <TermsOfServicePage showBackButton />}
          </div>
        </div>
      </main>

      {/* Company picker modal — "Find my company" affordance from the My
          Company section. Mirrors the SP mobile ViewProfileScreen picker;
          search is debounced in a useEffect above. */}
      {showCompanyPickerModal && (
        <div
          className="sp-company-picker-overlay"
          onClick={() => setShowCompanyPickerModal(false)}
        >
          <div
            className="sp-company-picker-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sp-company-picker-header">
              <h3>Find your company</h3>
              <button
                type="button"
                className="sp-company-picker-close"
                onClick={() => setShowCompanyPickerModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <input
              type="text"
              className="profile-input"
              value={companySearchQuery}
              onChange={(e) => setCompanySearchQuery(e.target.value)}
              placeholder="Search by company name, email, or phone"
              autoFocus
            />
            <div className="sp-company-picker-results">
              {companySearchQuery.trim().length < 2 ? (
                <p className="sp-company-picker-empty">
                  Start typing a company name, email, or phone to search.
                </p>
              ) : companySearching ? (
                <p className="sp-company-picker-empty">Searching…</p>
              ) : companySearchResults.length === 0 ? (
                <p className="sp-company-picker-empty">No matching companies found.</p>
              ) : (
                companySearchResults.map((row) => (
                  <button
                    key={row.userId}
                    type="button"
                    className="sp-company-picker-row"
                    onClick={() => handleCompanyPick(row)}
                  >
                    {row.companyLogoUrl &&
                      (row.companyLogoUrl.startsWith('http://') ||
                       row.companyLogoUrl.startsWith('https://') ||
                       row.companyLogoUrl.startsWith('data:')) ? (
                      <img
                        src={row.companyLogoUrl}
                        alt={row.businessName || 'Company'}
                        className="sp-company-picker-logo"
                      />
                    ) : (
                      // Auth-gated relative path (/api/v1/users/{id}/company-logo)
                      // — resolving it per-row would mean N Bearer-fetches on
                      // every keystroke. Fallback initial keeps the list fast;
                      // the real logo renders in the card once picked.
                      <div className="sp-company-picker-logo sp-parent-company-logo-fallback">
                        {(row.businessName || 'C').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="sp-company-picker-rowbody">
                      <p className="sp-parent-company-name">
                        {row.businessName || 'Company'}
                      </p>
                      {(row.city || row.state) && (
                        <p className="sp-parent-company-meta">
                          {[row.city, row.state].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {row.email && (
                        <p className="sp-parent-company-meta">{row.email}</p>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpProfilePage;
